import type { RepairApproval, RepairLevel, RepairRecord } from '@avisoma/shared';

/**
 * Shared presentation helpers for the Repair Report family (DR-4817). The page view and the
 * entity's consolidated card read their labels from here, so a repair level or an approval never
 * renders as its raw enum key in one place and a human phrase in another.
 *
 * The date `…Raw` / `…Label` evidence lanes stay out of these helpers; they remain visible
 * through the row inspector and the page's debug payload.
 */

const REPAIR_LEVEL_LABELS: Record<RepairLevel, string> = {
  repaired: 'Repaired',
  overhauled: 'Overhauled',
  modified: 'Modified',
  inspected: 'Inspected',
  tested: 'Tested',
  unknown: 'Unknown',
};

const APPROVAL_KIND_LABELS: Record<RepairApproval['kind'], string> = {
  oem: 'OEM',
  der: 'DER',
  'repair-station': 'Repair station',
  unknown: 'Unknown',
};

// Maps rather than indexed lookups: results reach the UI as raw jsonb, so a stored value of
// `"toString"` must not resolve through Object.prototype.
const REPAIR_LEVEL_BY_KEY = new Map<string, string>(Object.entries(REPAIR_LEVEL_LABELS));
const APPROVAL_KIND_BY_KEY = new Map<string, string>(Object.entries(APPROVAL_KIND_LABELS));

/**
 * The repair level as shown: the derived level, with the printed phrase beside it when they
 * read differently ("Inspected (IN SERVICEABLE CONDITION)"). An unknown level shows the printed
 * phrase alone.
 */
export function formatRepairLevel(record: Pick<RepairRecord, 'repairLevel' | 'repairLevelRaw'>): string | undefined {
  const raw = record.repairLevelRaw?.trim() || undefined;
  const label = record.repairLevel ? (REPAIR_LEVEL_BY_KEY.get(record.repairLevel) ?? record.repairLevel) : undefined;
  if (label === undefined || record.repairLevel === 'unknown') return raw ?? label;
  if (raw === undefined || raw.toLowerCase() === label.toLowerCase()) return label;
  return `${label} (${raw})`;
}

/** The approval as shown: its kind and, when printed, its number ("Repair station D.300023"). */
export function formatRepairApproval(record: Pick<RepairRecord, 'approval' | 'approvalRaw'>): string | undefined {
  const approval = record.approval;
  const raw = record.approvalRaw?.trim() || undefined;
  if (approval == null || typeof approval !== 'object') return raw;
  const kind = APPROVAL_KIND_BY_KEY.get(approval.kind) ?? approval.kind;
  if (approval.kind === 'unknown') return raw ?? kind;
  return approval.reference ? `${kind} ${approval.reference}` : kind;
}

/** The repaired unit as one label: "1211313-010 S/N YG639", or whichever half is printed. */
export function formatRepairPart(record: Pick<RepairRecord, 'partNumber' | 'serialNumber'>): string | undefined {
  const parts = [record.partNumber, record.serialNumber && `S/N ${record.serialNumber}`].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/** A since-new clock as shown, with its printed caption beside it: "13200 (Engine TT)" (DR-5329). */
export function formatRepairClock(value?: number, label?: string): string | undefined {
  if (typeof value !== 'number') return undefined;
  const caption = label?.trim();
  return caption ? `${value} (${caption})` : String(value);
}

/**
 * The record's present scalars as label/value pairs, in reading order. Absent fields are dropped
 * so a continuation page renders as a short card rather than a grid of em dashes.
 */
export function presentRepairFields(record: RepairRecord): [string, string][] {
  const fields: [string, string | undefined][] = [
    ['Subject', record.subject],
    ['P/N', record.partNumber],
    ['S/N', record.serialNumber],
    ['Description', record.description],
    ['Ex-engine ESN', record.esn],
    ['Work Order', record.workOrder],
    ['Repair Order', record.repairOrder],
    ['Repair Station', record.repairStation],
    ['Received', record.dateReceived],
    ['Completed', record.dateCompleted],
    ['Removed', record.removalDate],
    ['Removal Reason', record.removalReason],
    ['TSN', formatRepairClock(record.tsn, record.tsnLabel)],
    ['CSN', formatRepairClock(record.csn, record.csnLabel)],
    ['Level', formatRepairLevel(record)],
    ['Approval', formatRepairApproval(record)],
  ];
  return fields.filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== '');
}
